import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { TableBody, TableCell, TableHead, TableRow } from '@mui/material';
import type { Trade } from '@monorepo/types';
import { TradingSystem } from '@monorepo/types';
import { formatBrokerDate } from '../../../table/utils';
import {
  StyledActionText,
  StyledCellContent,
  StyledFeeText,
  StyledPriceFeeCell,
  StyledSideActionCell,
  StyledSideTag,
  StyledTable,
  StyledTableCellRight,
  StyledTableContainer,
  StyledTradeId,
} from '../styled';
import {
  formatCurrency,
  getActionKey,
  getSideTagColor,
  TRADE_DATE_FORMAT,
} from '../utils';

interface TradesTableProps {
  trades: Trade[];
  positionState: 'open' | 'closed';
}

export const TradesTable: FC<TradesTableProps> = ({
  trades,
  positionState,
}) => {
  const { t } = useTranslation(['position', 'common']);

  if (trades.length === 0) {
    return null;
  }

  return (
    <StyledTableContainer>
      <StyledTable>
        <TableHead>
          <TableRow>
            <TableCell>{t('position:trades_table.trade_id')}</TableCell>
            <TableCell>{t('position:trades_table.time')}</TableCell>
            <TableCell className="mobile-hide">
              {t('position:positions.side')}
            </TableCell>
            <TableCell className="mobile-hide">
              {t('position:trades_table.action')}
            </TableCell>
            <StyledTableCellRight className="mobile-hide">
              {t('position:trades_table.price')}
            </StyledTableCellRight>
            <StyledTableCellRight className="mobile-hide">
              {t('common:fee')}
            </StyledTableCellRight>
            <StyledSideActionCell className="desktop-hide">
              {t('position:positions.side')}/{t('position:trades_table.action')}
            </StyledSideActionCell>
            <StyledPriceFeeCell className="desktop-hide">
              {t('position:trades_table.price')}/{t('common:fee')}
            </StyledPriceFeeCell>
            <StyledTableCellRight>
              {t('position:positions.qty')}
            </StyledTableCellRight>
          </TableRow>
        </TableHead>
        <TableBody>
          {trades.map((trade, index) => {
            const actionKey = getActionKey(
              trade,
              index,
              trades.length,
              positionState,
            );
            const actionText = t(actionKey);

            return (
              <TableRow key={trade.id}>
                <TableCell>
                  <StyledTradeId>{trade.id}</StyledTradeId>
                </TableCell>
                <TableCell>
                  {formatBrokerDate(
                    trade.date,
                    TRADE_DATE_FORMAT,
                    TradingSystem.BlackArrowFutures,
                  )}
                </TableCell>
                <TableCell className="mobile-hide">
                  <StyledSideTag
                    label={trade.side}
                    color={getSideTagColor(trade.side)}
                    size="small"
                  />
                </TableCell>
                <TableCell className="mobile-hide">{actionText}</TableCell>
                <StyledTableCellRight className="mobile-hide">
                  {formatCurrency(trade.price)}
                </StyledTableCellRight>
                <StyledTableCellRight className="mobile-hide">
                  {formatCurrency(trade.fee)}
                </StyledTableCellRight>
                <StyledSideActionCell className="desktop-hide">
                  <StyledCellContent>
                    <StyledSideTag
                      label={trade.side}
                      color={getSideTagColor(trade.side)}
                      size="small"
                    />
                    <StyledActionText>{actionText}</StyledActionText>
                  </StyledCellContent>
                </StyledSideActionCell>
                <StyledPriceFeeCell className="desktop-hide">
                  <StyledCellContent alignItems="flex-end">
                    <span>{formatCurrency(trade.price)}</span>
                    <StyledFeeText>
                      {t('common:fee')}: {formatCurrency(trade.fee)}
                    </StyledFeeText>
                  </StyledCellContent>
                </StyledPriceFeeCell>
                <StyledTableCellRight>{trade.quantity}</StyledTableCellRight>
              </TableRow>
            );
          })}
        </TableBody>
      </StyledTable>
    </StyledTableContainer>
  );
};
